import React, { useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button, Col, Form, Modal, Row, Spinner } from "react-bootstrap";
import axios from "axios";
import { useLanguage } from "../../i18n/LanguageContext";
import { useAuth } from "../../components/auth/AuthProvider";
import apiUrl from "../../config/api";
import QuizScreen from "./components/QuizScreen";
import {
  filterQuestionsByView,
  getQuestionFilterAvailability,
  getQuestionsByType,
  orderQuestionsForQuiz,
  selectQuestionsForQuiz,
} from "../../utils/quizQuestionSelection";

const readStoredJson = (key, fallback = {}) => {
  try {
    const storedValue = localStorage.getItem(key);
    return storedValue ? JSON.parse(storedValue) : fallback;
  } catch (error) {
    return fallback;
  }
};

const setStoredValue = (key, value) => {
  try {
    localStorage.setItem(key, value);
  } catch (error) {
    console.warn("Unable to save local preference:", error);
  }
};

const shuffleItems = (items) => {
  const shuffledItems = [...items];

  for (let index = shuffledItems.length - 1; index > 0; index -= 1) {
    const randomIndex = Math.floor(Math.random() * (index + 1));
    [shuffledItems[index], shuffledItems[randomIndex]] = [shuffledItems[randomIndex], shuffledItems[index]];
  }

  return shuffledItems;
};

const orderAnswers = (answers = [], answerOrder) => {
  if (!Array.isArray(answers)) {
    return [];
  }

  const answersWithStableIds = answers.map((answer, answerIndex) => ({
    ...answer,
    _answerId: answer._answerId || `${answer.questionId || ""}-answer-${answerIndex}`,
  }));

  if (answerOrder === "random") {
    return shuffleItems(answersWithStableIds);
  }

  if (answerOrder === "correctFirst") {
    const orderedAnswers = [...answersWithStableIds];
    const correctAnswerIndex = orderedAnswers.findIndex((answer) => answer.isCorrect);

    if (correctAnswerIndex > 0) {
      [orderedAnswers[0], orderedAnswers[correctAnswerIndex]] = [orderedAnswers[correctAnswerIndex], orderedAnswers[0]];
    }

    return orderedAnswers;
  }

  return [...answersWithStableIds];
};

const QuizBuilder = () => {
  const { t } = useLanguage();
  const { isAuthLoading, userDetails } = useAuth();
  const text = t.quizPage;
  const subjectText = t.subjectsPage;
  const storageKey = `quizify-quiz-builder-${userDetails?.email || userDetails?.id || "local"}`;

  const [archives, setArchives] = useState([]);
  const [selectedArchiveId, setSelectedArchiveId] = useState("");
  const [subjects, setSubjects] = useState([]);
  const [subjectQuestionsById, setSubjectQuestionsById] = useState({});
  const [subjectSettings, setSubjectSettings] = useState({});
  const [quizParams, setQuizParams] = useState({
    questionType: "Closed",
    timer: 0,
    questionOrder: "inserted",
    answerOrder: "inserted",
    questionFilter: "all",
    showKeywords: true,
    autoShowOpenAnswers: false,
  });
  const [isLoadingArchives, setIsLoadingArchives] = useState(true);
  const [isLoadingSubjects, setIsLoadingSubjects] = useState(false);
  const [error, setError] = useState("");
  const [quizQuestions, setQuizQuestions] = useState([]);
  const [showQuizScreen, setShowQuizScreen] = useState(false);
  const [loadedStorageKey, setLoadedStorageKey] = useState("");
  const [resumePromptAttempt, setResumePromptAttempt] = useState(null);
  const [isCheckingResumeAttempt, setIsCheckingResumeAttempt] = useState(false);
  const [isLoadingResumeAttempt, setIsLoadingResumeAttempt] = useState(false);
  const [isStartingQuiz, setIsStartingQuiz] = useState(false);
  const [initialAttempt, setInitialAttempt] = useState(null);

  const token = localStorage.getItem("token") || sessionStorage.getItem("token");
  const authHeaders = useMemo(() => ({
    Authorization: `Bearer ${token}`,
    Accept: "application/json",
  }), [token]);

  useEffect(() => {
    if (isAuthLoading) {
      return;
    }

    const savedSettings = readStoredJson(storageKey);

    if (savedSettings.quizParams) {
      setQuizParams((previousParams) => ({ ...previousParams, ...savedSettings.quizParams }));
    }

    if (savedSettings.selectedArchiveId) {
      setSelectedArchiveId(savedSettings.selectedArchiveId);
    }

    setLoadedStorageKey(storageKey);
  }, [isAuthLoading, storageKey]);

  useEffect(() => {
    if (isAuthLoading) {
      setIsLoadingArchives(true);
      return;
    }

    if (!token) {
      setIsLoadingArchives(false);
      return;
    }

    axios.post(`${apiUrl}/getArchives`, {}, { headers: authHeaders })
      .then((response) => {
        const nextArchives = response.data || [];
        const savedSettings = readStoredJson(storageKey);
        const savedArchiveExists = nextArchives.some((archive) => archive._id === savedSettings.selectedArchiveId);
        setArchives(nextArchives);
        setSelectedArchiveId(savedArchiveExists ? savedSettings.selectedArchiveId : nextArchives[0]?._id || "");
      })
      .catch((requestError) => {
        setError(requestError.response?.data?.message || requestError.message);
      })
      .finally(() => {
        setIsLoadingArchives(false);
      });
  }, [authHeaders, isAuthLoading, storageKey, token]);

  useEffect(() => {
    if (isAuthLoading || !token) {
      return;
    }

    if (!selectedArchiveId) {
      setSubjects([]);
      setSubjectQuestionsById({});
      setSubjectSettings({});
      return;
    }

    const savedSettings = readStoredJson(storageKey);

    setIsLoadingSubjects(true);
    setError("");

    axios.post(`${apiUrl}/getSubjects`, { archiveId: selectedArchiveId }, { headers: authHeaders })
      .then(async (response) => {
        const nextSubjects = response.data || [];
        const questionsEntries = await Promise.all(nextSubjects.map(async (subject) => {
          const questionsResponse = await axios.post(`${apiUrl}/getQuestions`, { subjectId: subject._id }, { headers: authHeaders });
          return [subject._id, questionsResponse.data || []];
        }));
        const questionsMap = Object.fromEntries(questionsEntries);

        setSubjects(nextSubjects);
        setSubjectQuestionsById(questionsMap);
        setSubjectSettings((previousSettings) => {
          const savedSubjectSettings = savedSettings.subjectSettings || {};
          const nextSettings = {};

          nextSubjects.forEach((subject) => {
            const subjectQuestions = getQuestionsByType(
              filterQuestionsByView(questionsMap[subject._id] || [], quizParams.questionFilter),
              quizParams.questionType
            );
            const savedSubject = previousSettings[subject._id] || savedSubjectSettings[subject._id] || {};
            const defaultCount = Math.min(10, subjectQuestions.length);

            nextSettings[subject._id] = {
              included: savedSubject.included ?? subjectQuestions.length > 0,
              count: Math.min(Number(savedSubject.count || defaultCount || 0), subjectQuestions.length),
              openCount: Number(savedSubject.openCount || 0),
            };
          });

          return nextSettings;
        });
      })
      .catch((requestError) => {
        setError(requestError.response?.data?.message || requestError.message);
      })
      .finally(() => {
        setIsLoadingSubjects(false);
      });
  }, [selectedArchiveId, authHeaders, isAuthLoading, storageKey, token]);

  useEffect(() => {
    if (isAuthLoading || !token || loadedStorageKey !== storageKey) {
      return;
    }

    const savedSettings = readStoredJson(storageKey);
    setStoredValue(storageKey, JSON.stringify({
      ...savedSettings,
      selectedArchiveId,
      quizParams,
      subjectSettings,
    }));
  }, [isAuthLoading, loadedStorageKey, quizParams, selectedArchiveId, storageKey, subjectSettings, token]);

  const selectedArchive = archives.find((archive) => archive._id === selectedArchiveId) || null;

  const subjectAvailability = useMemo(() => subjects.reduce((availability, subject) => {
    const filteredQuestions = filterQuestionsByView(subjectQuestionsById[subject._id] || [], quizParams.questionFilter);
    const typedQuestions = getQuestionsByType(filteredQuestions, quizParams.questionType);

    availability[subject._id] = {
      total: typedQuestions.length,
      open: filteredQuestions.filter((question) => question.questionType === "open").length,
      closed: filteredQuestions.filter((question) => question.questionType === "closed").length,
    };

    return availability;
  }, {}), [quizParams.questionFilter, quizParams.questionType, subjectQuestionsById, subjects]);

  const quizFilterAvailability = useMemo(() => {
    const typedQuestions = subjects.flatMap((subject) => (
      getQuestionsByType(subjectQuestionsById[subject._id] || [], quizParams.questionType)
    ));
    return getQuestionFilterAvailability(typedQuestions);
  }, [quizParams.questionType, subjectQuestionsById, subjects]);

  useEffect(() => {
    if (quizParams.questionFilter !== "all" && !quizFilterAvailability[quizParams.questionFilter]) {
      setQuizParams((previousParams) => ({ ...previousParams, questionFilter: "all" }));
    }
  }, [quizFilterAvailability, quizParams.questionFilter]);

  const selectedQuestionTotal = subjects.reduce((total, subject) => {
    const subjectSetting = subjectSettings[subject._id];
    const availableQuestions = subjectAvailability[subject._id]?.total || 0;

    if (!subjectSetting?.included || !availableQuestions) {
      return total;
    }

    return total + Math.min(Number(subjectSetting.count || 0), availableQuestions);
  }, 0);

  const updateQuizParams = (event) => {
    const { name, value, checked, type } = event.target;
    setQuizParams((previousParams) => ({
      ...previousParams,
      [name]: type === "checkbox"
        ? checked
        : name === "timer"
          ? Math.max(0, Math.min(Number(value || 0), 480))
          : value,
    }));
  };

  const updateSubjectSetting = (subjectId, nextSetting) => {
    setSubjectSettings((previousSettings) => ({
      ...previousSettings,
      [subjectId]: {
        ...previousSettings[subjectId],
        ...nextSetting,
      },
    }));
  };

  const setAllSubjectsIncluded = (included) => {
    setSubjectSettings((previousSettings) => {
      const nextSettings = { ...previousSettings };

      subjects.forEach((subject) => {
        const availableQuestions = subjectAvailability[subject._id]?.total || 0;
        const currentSetting = nextSettings[subject._id] || {};

        nextSettings[subject._id] = {
          ...currentSetting,
          included: included && availableQuestions > 0,
        };
      });

      return nextSettings;
    });
  };

  const buildSubjectQuestions = (subject) => {
    const subjectSetting = subjectSettings[subject._id] || {};
    const filteredQuestions = filterQuestionsByView(subjectQuestionsById[subject._id] || [], quizParams.questionFilter);
    const quizType = quizParams.questionType;
    const requestedCount = Math.min(Number(subjectSetting.count || 0), subjectAvailability[subject._id]?.total || 0);
    const selectionOptions = {
      filterValue: quizParams.questionFilter,
      order: quizParams.questionOrder,
    };
    let selectedQuestions = [];

    if (quizType === "Both") {
      const openQuestions = filteredQuestions.filter((question) => question.questionType === "open");
      const closedQuestions = filteredQuestions.filter((question) => question.questionType === "closed");
      const requestedOpenCount = Math.min(Number(subjectSetting.openCount || 0), openQuestions.length, requestedCount);
      const requestedClosedCount = Math.max(0, requestedCount - requestedOpenCount);

      selectedQuestions = [
        ...selectQuestionsForQuiz(closedQuestions, requestedClosedCount, selectionOptions),
        ...selectQuestionsForQuiz(openQuestions, requestedOpenCount, selectionOptions),
      ];
      selectedQuestions = orderQuestionsForQuiz(selectedQuestions, selectionOptions);
    } else {
      selectedQuestions = selectQuestionsForQuiz(
        getQuestionsByType(filteredQuestions, quizType),
        requestedCount,
        selectionOptions
      );
    }

    return selectedQuestions.map((question) => ({
      ...question,
      subjectName: subject.name,
      answers: question.questionType === "closed"
        ? orderAnswers((question.answers || []).map((answer, answerIndex) => ({
          ...answer,
          _answerId: answer._answerId || `${question._id}-answer-${answerIndex}`,
        })), quizParams.answerOrder)
        : question.answers,
    }));
  };

  const loadAttemptIntoQuiz = async (attemptId) => {
    try {
      setIsLoadingResumeAttempt(true);
      const response = await axios.get(`${apiUrl}/quizAttempts/${attemptId}`, {
        headers: authHeaders,
      });
      const attempt = response.data.attempt || {};
      const loadedQuestions = response.data.questions || [];
      const restoredQuizParams = {
        ...quizParams,
        ...(attempt.quizConfig || {}),
        numberOfQuestions: loadedQuestions.length || Number(attempt.requestedQuestions || 0),
      };

      setQuizParams(restoredQuizParams);
      setQuizQuestions(loadedQuestions);
      setInitialAttempt({
        _id: attempt._id,
        startedAt: attempt.startedAt,
        selectedAnswers: loadedQuestions.map((question) => question.selectedAnswer ?? null),
        timeRemainingSeconds: attempt.timeRemainingSeconds,
        currentQuestionIndex: attempt.currentQuestionIndex,
      });
      setResumePromptAttempt(null);
      setShowQuizScreen(true);
      setError("");
    } catch (requestError) {
      setError(requestError.response?.data?.message || text.resumeQuizLoadError);
    } finally {
      setIsLoadingResumeAttempt(false);
    }
  };

  const createInitialQuizAttempt = async (orderedQuestions, nextQuizParams) => {
    const startedAt = new Date();
    const response = await axios.post(`${apiUrl}/quizAttempts/start`, {
      subjectId: null,
      archiveId: selectedArchive?._id || null,
      quizParams: nextQuizParams,
      startedAt: startedAt.toISOString(),
      questions: orderedQuestions.map((question, index) => ({
        questionId: question._id,
        wasViewed: index === 0,
        wasAnswered: false,
        selectedAnswer: null,
        answerOrder: (question.answers || []).map((answer, answerIndex) => (
          answer._answerId || `${question._id}-answer-${answerIndex}`
        )),
      })),
    }, {
      headers: {
        ...authHeaders,
        'Content-Type': 'application/json',
      },
    });

    return {
      _id: response.data.attemptId,
      startedAt: startedAt.toISOString(),
      selectedAnswers: Array(orderedQuestions.length).fill(null),
      timeRemainingSeconds: Number(nextQuizParams.timer || 0) * 60,
      currentQuestionIndex: 0,
    };
  };

  const confirmCurrentAttemptAndStartNew = async () => {
    if (!resumePromptAttempt?._id) {
      handleStartQuiz({ skipResumeCheck: true });
      return;
    }

    try {
      setIsLoadingResumeAttempt(true);
      await axios.put(`${apiUrl}/quizAttempts/${resumePromptAttempt._id}/complete-progress`, {}, {
        headers: authHeaders,
      });
      setResumePromptAttempt(null);
      await handleStartQuiz({ skipResumeCheck: true });
    } catch (requestError) {
      setError(requestError.response?.data?.message || text.resumeQuizLoadError);
    } finally {
      setIsLoadingResumeAttempt(false);
    }
  };

  const handleStartQuiz = async ({ skipResumeCheck = false } = {}) => {
    if (!skipResumeCheck && selectedArchive?._id) {
      try {
        setIsCheckingResumeAttempt(true);
        const response = await axios.get(`${apiUrl}/quizAttempts/in-progress/latest`, {
          headers: authHeaders,
          params: { archiveId: selectedArchive._id },
        });

        if (response.data.attempt?._id) {
          setResumePromptAttempt(response.data.attempt);
          return;
        }
      } catch (requestError) {
        setError(requestError.response?.data?.message || text.resumeQuizLoadError);
        return;
      } finally {
        setIsCheckingResumeAttempt(false);
      }
    }

    const selectedQuestions = subjects
      .filter((subject) => subjectSettings[subject._id]?.included)
      .flatMap(buildSubjectQuestions);
    const orderedQuestions = quizParams.questionOrder === "random"
      ? shuffleItems(selectedQuestions)
      : selectedQuestions.sort((firstQuestion, secondQuestion) => Number(firstQuestion._id) - Number(secondQuestion._id));

    if (!orderedQuestions.length) {
      setError(text.noQuestionsSelected);
      return;
    }

    try {
      setIsStartingQuiz(true);
      setError("");
      const nextQuizParams = {
        ...quizParams,
        numberOfQuestions: orderedQuestions.length,
      };
      const startedAttempt = await createInitialQuizAttempt(orderedQuestions, nextQuizParams);

      setInitialAttempt(startedAttempt);
      setQuizQuestions(orderedQuestions);
      setShowQuizScreen(true);
    } catch (requestError) {
      setError(requestError.response?.data?.message || text.resumeQuizLoadError);
    } finally {
      setIsStartingQuiz(false);
    }
  };

  if (showQuizScreen) {
    const includedSubjects = subjects.filter((subject) => subjectSettings[subject._id]?.included);
    const singleIncludedSubject = includedSubjects.length === 1 ? includedSubjects[0] : null;

    return (
      <QuizScreen
        breakpoint={true}
        subject={{
          _id: `archive-${selectedArchive?._id}`,
          archiveId: selectedArchive?._id,
          name: selectedArchive?.name || text.title,
          subjectName: singleIncludedSubject?.name || '',
          archiveName: selectedArchive?.name || '',
          desc: selectedArchive?.desc || "",
          isArchiveQuiz: true,
        }}
        quizParams={{
          ...quizParams,
          numberOfQuestions: quizQuestions.length,
        }}
        questions={quizQuestions}
        initialAttempt={initialAttempt}
        setShowQuizScreen={setShowQuizScreen}
      />
    );
  }

  return (
    <Row>
      <Col lg="12">
        <div className="card" data-aos="fade-up">
          <div className="card-body">
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
              <div>
                <p className="mb-1 text-muted">{t.nav.quizWip}</p>
                <h3 className="mb-1">{text.title}</h3>
                <p className="mb-0 text-muted">{text.subtitle}</p>
              </div>
              <Badge bg="primary" className="fs-6 px-3 py-2">{text.totalQuestions(selectedQuestionTotal)}</Badge>
            </div>

            {error && <Alert variant="danger">{error}</Alert>}

            {isLoadingArchives ? (
              <div className="py-5 text-center">
                <Spinner animation="border" variant="primary" />
              </div>
            ) : !archives.length ? (
              <Alert variant="info" className="mb-0">{text.noArchives}</Alert>
            ) : (
              <>
                <Row className="g-3 mb-4">
                  <Col lg="4">
                    <Form.Group>
                      <Form.Label>{text.selectArchive}</Form.Label>
                      <Form.Select value={selectedArchiveId} onChange={(event) => setSelectedArchiveId(event.target.value)}>
                        {archives.map((archive) => (
                          <option key={archive._id} value={archive._id}>{archive.name}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col lg="2" md="6">
                    <Form.Group>
                      <Form.Label>{subjectText.quizType}</Form.Label>
                      <Form.Select name="questionType" value={quizParams.questionType} onChange={updateQuizParams}>
                        <option value="Closed">{subjectText.closed}</option>
                        <option value="Open">{subjectText.open}</option>
                        <option value="Both">{subjectText.both}</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col lg="2" md="6">
                    <Form.Group>
                      <Form.Label>{subjectText.questionFilter}</Form.Label>
                      <Form.Select name="questionFilter" value={quizParams.questionFilter} onChange={updateQuizParams}>
                        <option value="all" disabled={!quizFilterAvailability.all}>
                          {subjectText.filterQuestionCount(subjectText.allSeenAndUnseenQuestions, quizFilterAvailability.all)}
                        </option>
                        <option value="unseen" disabled={!quizFilterAvailability.unseen}>
                          {subjectText.filterQuestionCount(subjectText.onlyUnseenQuestions, quizFilterAvailability.unseen)}
                        </option>
                        <option value="seen" disabled={!quizFilterAvailability.seen}>
                          {subjectText.filterQuestionCount(subjectText.onlySeenQuestions, quizFilterAvailability.seen)}
                        </option>
                        <option value="progressive" disabled={!quizFilterAvailability.progressive}>
                          {subjectText.filterQuestionCount(subjectText.progressiveReview, quizFilterAvailability.progressive)}
                        </option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col lg="2" md="6">
                    <Form.Group>
                      <Form.Label>{subjectText.questionOrder}</Form.Label>
                      <Form.Select name="questionOrder" value={quizParams.questionOrder} onChange={updateQuizParams}>
                        <option value="inserted">{subjectText.insertedOrder}</option>
                        <option value="random">{subjectText.randomOrder}</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  {['Open', 'Both'].includes(quizParams.questionType) && (
                    <Col lg="4" md="6">
                      <Form.Group className="pt-lg-4">
                        <Form.Check
                          type="switch"
                          id="quiz-builder-auto-open-answers"
                          name="autoShowOpenAnswers"
                          label={subjectText.automaticOpenAnswers}
                          checked={quizParams.autoShowOpenAnswers === true}
                          onChange={updateQuizParams}
                        />
                      </Form.Group>
                    </Col>
                  )}
                  <Col lg="2" md="6">
                    <Form.Group>
                      <Form.Label>{subjectText.answerOrder}</Form.Label>
                      <Form.Select name="answerOrder" value={quizParams.answerOrder} onChange={updateQuizParams}>
                        <option value="inserted">{subjectText.answerInsertedOrder}</option>
                        <option value="random">{subjectText.answerRandomOrder}</option>
                        <option value="correctFirst">{subjectText.correctAnswerFirst}</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col lg="2" md="6">
                    <Form.Group>
                      <Form.Label>{subjectText.timer}</Form.Label>
                      <Form.Control name="timer" type="number" min="0" max="480" value={quizParams.timer} onChange={updateQuizParams} />
                    </Form.Group>
                  </Col>
                  <Col lg="2" md="6">
                    <Form.Group className="pt-lg-4">
                      <Form.Check
                        type="switch"
                        id="quiz-builder-show-keywords"
                        name="showKeywords"
                        label={subjectText.showKeywords}
                        checked={quizParams.showKeywords !== false}
                        onChange={updateQuizParams}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <div className="quiz-builder-subject-toolbar">
                  <div>
                    <h5 className="mb-1">{text.selectSubjectsTitle}</h5>
                    <p className="mb-0 text-muted">{text.selectSubjectsHelp}</p>
                  </div>
                  <div className="quiz-builder-subject-actions">
                    <Button variant="soft-primary" size="sm" onClick={() => setAllSubjectsIncluded(true)}>
                      {text.selectAllSubjects}
                    </Button>
                    <Button variant="soft-secondary" size="sm" onClick={() => setAllSubjectsIncluded(false)}>
                      {text.deselectAllSubjects}
                    </Button>
                    <Button variant="primary" disabled={!selectedQuestionTotal || isLoadingSubjects || isCheckingResumeAttempt || isStartingQuiz} onClick={() => handleStartQuiz()}>
                      {isCheckingResumeAttempt || isStartingQuiz ? (
                        <Spinner animation="border" size="sm" />
                      ) : text.startGeneratedQuiz}
                    </Button>
                  </div>
                </div>

                {isLoadingSubjects ? (
                  <div className="py-5 text-center">
                    <Spinner animation="border" variant="primary" />
                  </div>
                ) : subjects.length ? (
                  <div className="quiz-builder-subjects">
                    {subjects.map((subject) => {
                      const setting = subjectSettings[subject._id] || {};
                      const available = subjectAvailability[subject._id] || { total: 0, open: 0, closed: 0 };
                      const safeCount = Math.min(Number(setting.count || 0), available.total);
                      const safeOpenCount = Math.min(Number(setting.openCount || 0), available.open, safeCount);

                      return (
                        <div className="quiz-builder-subject-row" key={subject._id}>
                          <div className="quiz-builder-subject-main">
                            <Form.Check
                              checked={Boolean(setting.included)}
                              disabled={!available.total}
                              onChange={(event) => updateSubjectSetting(subject._id, { included: event.target.checked })}
                            />
                            <div className="min-w-0">
                              <h6 className="mb-1 text-truncate">{subject.name}</h6>
                              <small className="text-muted">
                                {text.availableBySubject(available.total, available.closed, available.open)}
                              </small>
                            </div>
                          </div>
                          <div className="quiz-builder-subject-controls">
                            <Form.Control
                              aria-label={text.subjectQuestionCount}
                              title={text.subjectQuestionCount}
                              type="number"
                              min="0"
                              max={available.total}
                              value={safeCount}
                              disabled={!setting.included || !available.total}
                              onChange={(event) => updateSubjectSetting(subject._id, {
                                count: Math.min(Number(event.target.value || 0), available.total),
                              })}
                            />
                            {quizParams.questionType === "Both" && (
                              <Form.Control
                                aria-label={text.subjectOpenQuestionCount}
                                title={text.subjectOpenQuestionCount}
                                type="number"
                                min="0"
                                max={Math.min(available.open, safeCount)}
                                value={safeOpenCount}
                                disabled={!setting.included || !available.open}
                                onChange={(event) => updateSubjectSetting(subject._id, {
                                  openCount: Math.min(Number(event.target.value || 0), available.open, safeCount),
                                })}
                              />
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <Alert variant="info" className="mb-0">{text.noSubjectsInArchive}</Alert>
                )}

                <div className="quiz-builder-footer-total mt-4">
                  <strong>{text.totalQuestions(selectedQuestionTotal)}</strong>
                </div>
              </>
            )}
          </div>
        </div>
      </Col>
      <Modal
        className="modal-theme-bg"
        show={Boolean(resumePromptAttempt)}
        onHide={() => !isLoadingResumeAttempt && setResumePromptAttempt(null)}
        centered
        backdrop="static"
      >
        <Modal.Header>
          <Modal.Title as="h5">{text.resumeQuizTitle}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-2">{text.resumeQuizText}</p>
          {resumePromptAttempt && (
            <small className="text-muted">
              {resumePromptAttempt.answeredQuestions}/{resumePromptAttempt.requestedQuestions || resumePromptAttempt.viewedQuestions} {t.dashboard.answers}
            </small>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            type="button"
            variant="soft-secondary"
            disabled={isLoadingResumeAttempt}
            onClick={() => setResumePromptAttempt(null)}
          >
            {text.close}
          </Button>
          <Button
            type="button"
            variant="soft-danger"
            disabled={isLoadingResumeAttempt}
            onClick={confirmCurrentAttemptAndStartNew}
          >
            {isLoadingResumeAttempt ? <Spinner animation="border" size="sm" /> : text.startNewQuiz}
          </Button>
          <Button
            type="button"
            variant="primary"
            disabled={isLoadingResumeAttempt}
            onClick={() => loadAttemptIntoQuiz(resumePromptAttempt._id)}
          >
            {isLoadingResumeAttempt ? <Spinner animation="border" size="sm" /> : text.continueQuiz}
          </Button>
        </Modal.Footer>
      </Modal>
    </Row>
  );
};

export default QuizBuilder;
